import { Component } from '@theme/component';
import { isClickedOutside, setCookie } from '@theme/utilities';

/**
 * Dropdown localization component — trigger button + language panel with auto-submit.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The trigger button.
 * @property {HTMLDivElement} panel - The dropdown panel.
 * @property {HTMLInputElement} languageInput - The hidden language_code input.
 *
 * @extends {Component<Refs>}
 */
class DropdownLocalizationComponent extends Component {
  get isOpen() {
    return this.hasAttribute('data-open');
  }

  /**
   * Selects a language and submits the form.
   *
   * @param {string} isoCode - The language ISO code.
   * @param {Event} event - The event object.
   */
  selectLanguage(isoCode, event) {
    event.preventDefault();

    this.refs.languageInput.value = isoCode;
    this.querySelector('form')?.submit();
  }

  /** Toggles the panel open/closed. */
  toggleSelector() {
    return this.isOpen ? this.hidePanel() : this.showPanel();
  }

  /** Shows the panel. */
  showPanel() {
    if (this.isOpen) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.setAttribute('data-open', '');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  /** Hides the panel. */
  hidePanel = () => {
    if (!this.isOpen) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.removeAttribute('data-open');
    this.refs.button?.setAttribute('aria-expanded', 'false');
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

/**
 * Drawer localization component — resets the form when the dialog closes.
 * Listens for `dialog:close` which fires after the closing animation completes.
 *
 * @extends {Component}
 */
class DrawerLocalizationComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#dialogComponent?.addEventListener('dialog:close', this.#onDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogComponent?.removeEventListener('dialog:close', this.#onDialogClose);
    this.#dialogComponent = null;
  }

  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  #onDialogClose = () => {
    const form = this.querySelector('form');
    form?.reset();
  };

  handleChangeLocation = () => {
    const countrySelected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="country_code"]:checked')
    );

    if (!countrySelected) {
      return;
    }

    const linkStore = countrySelected.value;

    setCookie('geolocationModal', "true", 1);
    window.location.href = linkStore;
  }
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
